import { z } from "zod";

export const memberSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string(),
  phone: z.string(),
  job_title: z.string(),
  locale: z.string(),
  status: z.string(),
  status_label: z.string(),
  email_verified_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export const memberDetailSchema = memberSchema.extend({
  organizations: z.array(
    z.object({
      id: z.number(),
      organization_id: z.string(),
      name: z.string(),
      role: z.string(),
      role_label: z.string(),
      is_primary: z.boolean(),
      is_active: z.boolean(),
    }),
  ),
  grants: z.array(
    z.object({
      id: z.number(),
      opportunity_id: z.string(),
      opportunity: z.string(),
      expires_at: z.string().nullable(),
      active: z.boolean(),
    }),
  ),
  submissions: z.array(
    z.object({
      id: z.string(),
      kind: z.string(),
      submitted_at: z.string(),
      subject: z.string(),
    }),
  ),
});
export const organizationOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  country_code: z.string(),
});
export const memberOrganizationSchema = z.object({
  id: z.number(),
  organization_id: z.string(),
  name: z.string(),
  role: z.string(),
  role_label: z.string(),
  is_primary: z.boolean(),
  is_active: z.boolean(),
});

export const opportunitySchema = z.object({
  id: z.string(),
  code: z.string(),
  title: z.string(),
  summary: z.string(),
  access_level: z.string(),
  body: z.string(),
  slug: z.string(),
  teaser_title: z.string(),
  teaser_summary: z.string(),
  location: z.string(),
  listing: z.string(),
  access_preset: z.string(),
  opens_at: z.string().nullable(),
  closes_at: z.string().nullable(),
  is_featured: z.boolean(),
  is_archived: z.boolean(),
  grant_count: z.number(),
  lifecycle_status: z.string().default("active"),
  publication_status: z.string().default("draft"),
  has_live_version: z.boolean().default(false),
  kind_label: z.string().default(""),
  sectors: z.array(z.string()).default([]),
  request_count: z.number().default(0),
  saver_count: z.number().default(0),
  updated_at: z.string(),
});
export const opportunityDetailSchema = opportunitySchema.extend({
  grants: z.array(
    z.object({
      id: z.number(),
      subject: z.string(),
      subject_kind: z.string(),
      subject_id: z.string(),
      expires_at: z.string().nullable(),
      active: z.boolean(),
    }),
  ),
});

export const grantSubjectSchema = z.object({
  id: z.string(),
  kind: z.enum(["member", "organization", "group"]),
  label: z.string(),
  description: z.string(),
});

export const submissionSchema = z.object({
  id: z.string(),
  kind: z.string(),
  kind_label: z.string(),
  name: z.string(),
  company_name: z.string(),
  email: z.string(),
  phone: z.string(),
  subject: z.string(),
  message: z.string(),
  locale: z.string(),
  submitted_at: z.string(),
  status: z.string(),
  status_label: z.string(),
  internal_note: z.string(),
  crm_reference: z.string(),
  document_count: z.number(),
  context: z.record(z.string(), z.unknown()),
});
export const submissionDetailSchema = submissionSchema.extend({
  answer_sections: z.array(z.object({title:z.string(),items:z.array(z.object({label:z.string(),value:z.string()}))})).optional().default([]),
  payload: z.record(z.string(), z.unknown()),
  documents: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      mime_type: z.string(),
      size: z.number(),
      scan_status: z.string(),
      downloadable: z.boolean(),
    }),
  ),
});

export const newsletterSchema = z.object({
  id: z.string(),
  email: z.string(),
  locale: z.string(),
  status: z.string(),
  status_label: z.string(),
  source: z.string(),
  source_label: z.string(),
  created_at: z.string(),
  confirmed_at: z.string().nullable(),
  unsubscribed_at: z.string().nullable(),
});

export const mediaSchema = z.object({
  id: z.string(),
  kind: z.string(),
  name: z.string(),
  mime_type: z.string(),
  size: z.number(),
  url: z.string(),
  focal_x: z.number(),
  focal_y: z.number(),
  source: z.string(),
  usage_count: z.number(),
  created_at: z.string(),
});
export const mediaDetailSchema = mediaSchema
  .omit({ usage_count: true })
  .extend({
    translations: z.record(
      z.string(),
      z.object({ title: z.string(), alt_text: z.string() }),
    ),
    usages: z.array(
      z.object({
        type: z.string(),
        type_label: z.string(),
        target: z.string(),
        field: z.string(),
        field_label: z.string(),
        href: z.string(),
      }),
    ),
  });

export const panelUserRowSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string(),
  role: z.string(),
  role_label: z.string(),
  is_active: z.boolean(),
  last_login: z.string().nullable(),
  totp_configured: z.boolean(),
  override_count: z.number(),
  manageable: z.boolean(),
  password_resettable: z.boolean(),
  overrides_editable: z.boolean(),
});
export const panelUserDetailSchema = panelUserRowSchema.extend({
  overrides: z.array(
    z.object({ capability: z.string(), effect: z.enum(["allow", "deny"]) }),
  ),
  available_capabilities: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      included_in_role: z.boolean(),
    }),
  ),
  sessions: z.array(
    z.object({
      id: z.string(),
      user_agent: z.string(),
      ip_hint: z.string(),
      started_at: z.string(),
      last_seen_at: z.string(),
      expires_at: z.string(),
      revoked_at: z.string().nullable(),
      active: z.boolean(),
    }),
  ),
});

export const panelSessionSchema = z.object({
  id: z.string(),
  current: z.boolean(),
  user_agent: z.string(),
  started_at: z.string(),
  last_seen_at: z.string(),
  expires_at: z.string(),
  revoked_at: z.string().nullable(),
});
export const notificationSchema = z.object({
  id: z.string(),
  title: z.string(),
  message: z.string(),
  severity: z.string(),
  target_path: z.string(),
  read_at: z.string().nullable(),
  created_at: z.string(),
});
export const notificationPreferenceSchema = z.object({
  event_key: z.string(),
  label: z.string(),
  required: z.boolean(),
  in_app_enabled: z.boolean(),
  email_enabled: z.boolean(),
});
export const auditEventSchema = z.object({
  id: z.number(),
  action: z.string(),
  action_label: z.string(),
  actor: z.string(),
  target: z.string(),
  success: z.boolean(),
  result: z.string(),
  changes: z.record(z.string(), z.unknown()),
  created_at: z.string(),
});
const dashboardCardSchema = z.object({
  key: z.string(),
  label: z.string(),
  value: z.number(),
  href: z.string(),
});

export const dashboardSchema = z.object({
  cards: z.array(dashboardCardSchema),
  queues: z.array(dashboardCardSchema).default([]),
  recent: z.array(
    z.object({
      id: z.string(),
      label: z.string(),
      domain: z.string(),
      domain_label: z.string(),
      locale: z.string(),
      status: z.string(),
      status_label: z.string(),
      updated_at: z.string(),
    }),
  ),
  allowed_widgets: z.array(z.string()).default([]),
  unread_notifications: z.number(),
  generated_at: z.string(),
});

const analyticsKpiSchema = z.object({
  key: z.string(),
  label: z.string(),
  value: z.number(),
  previous_value: z.number().nullable(),
  change_percent: z.number().nullable(),
  href: z.string().optional(),
});

const dateSeriesPointSchema = z.object({
  date: z.string(),
  value: z.number(),
});

const breakdownPointSchema = z.object({
  key: z.string(),
  label: z.string(),
  value: z.number(),
});

export const dashboardOperationsSchema = z.object({
  range: z.enum(["7d", "30d", "90d", "custom"]),
  compare: z.enum(["previous", "none"]),
  start: z.string(),
  end: z.string(),
  generated_at: z.string(),
  kpis: z.array(analyticsKpiSchema),
  series: z.object({
    submissions: z.array(dateSeriesPointSchema),
    publications: z.array(dateSeriesPointSchema),
    members: z.array(dateSeriesPointSchema),
  }),
  breakdowns: z.object({
    submission_status: z.array(breakdownPointSchema),
    workflow_status: z.array(breakdownPointSchema),
    member_status: z.array(breakdownPointSchema),
  }),
  health: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      value: z.number(),
      status: z.enum(["healthy", "attention"]),
      href: z.string(),
    }),
  ),
});

export const dashboardSiteAnalyticsSchema = z.object({
  status: z.enum(["ready", "unconfigured", "stale", "error"]),
  generated_at: z.string(),
  stale: z.boolean(),
  message: z.string().optional(),
  range: z.enum(["7d", "30d", "90d", "custom"]),
  compare: z.enum(["previous", "none"]),
  start: z.string(),
  end: z.string(),
  kpis: z.array(analyticsKpiSchema),
  traffic: z.array(
    z.object({
      date: z.string(),
      users: z.number(),
      sessions: z.number(),
      views: z.number(),
      conversions: z.number(),
    }),
  ),
  sources: z.array(
    breakdownPointSchema.extend({ percentage: z.number() }),
  ),
  devices: z.array(breakdownPointSchema),
  top_pages: z.array(
    z.object({ path: z.string(), title: z.string(), views: z.number() }),
  ),
});

export const dashboardPreferencesSchema = z.object({
  widgets: z.array(
    z.object({
      key: z.string(),
      x: z.number().int(),
      y: z.number().int(),
      w: z.union([z.literal(4), z.literal(6), z.literal(8), z.literal(12)]),
      h: z.number().int(),
      hidden: z.boolean(),
    }),
  ),
  default_range: z.enum(["7d", "30d", "90d"]),
  version: z.number().int(),
});

export const savedViewSchema = z.object({
  id: z.string(),
  name: z.string(),
  configuration: z.record(z.string(), z.unknown()),
  is_default: z.boolean(),
});

export const previewTicketSchema = z.object({
  url: z.string().url(),
  expires_at: z.string(),
});

export const mediaUpdateResultSchema = z.object({
  id: z.string(),
  updated_at: z.string(),
});

export const mediaArchiveResultSchema = z.object({
  id: z.string(),
  archived: z.boolean(),
});

export const sessionRevokeResultSchema = z.object({
  revoked: z.boolean(),
  current: z.boolean(),
});

export const newsletterActionResultSchema = z.object({
  id: z.string(),
  status: z.string(),
});

export const roleOptionSchema = z.object({
  key: z.string(),
  label: z.string(),
  assignable: z.boolean().optional().default(false),
});
export const rolePackageSchema = roleOptionSchema.extend({
  description: z.string(),
  user_count: z.number(),
  editable: z.boolean().optional().default(false),
  capabilities: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
    }),
  ),
});
export const panelInvitationResultSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  role: z.string(),
  creation_method: z.enum(["invite", "password"]),
});
